import { Region } from '@heidi/schema/ml-scribe';

/**
 * The CrabNebula update channel a desktop client's authenticated region is
 * served from. `'stable'` is the unnamed / ROW channel; `'uk'` and `'eu'` are
 * the controlled (UK-MDR / EU-MDR CE) channels held as draft until a Notified-
 * Body-approved promotion (APP-6116 §5.14).
 */
export type UpdateChannel = 'uk' | 'eu' | 'stable';

/**
 * SOURCE OF TRUTH (telemetry copy) for which regions are CE/MDR-controlled on
 * desktop. Controlled: UK (`'uk'`), EU (`'eu'`), and SE-AZ1 (`'eu'`) — Sweden
 * (Enterprise) is an EU member state, so it is EU-MDR controlled, matching how
 * the rest of the app already treats it (`flags.ts`
 * UNIFIED_COLLECTIONS_BLOCKED_REGIONS — "Sweden is an EU member state", the
 * `europeanUnion` coding config, `useEvidenceAccess`). Every other `*-AZ1` cell
 * region is ROW / `'stable'`.
 *
 * ⚠️ This is the **second copy** of the closed region→channel map. The first —
 * and the only one that gates the updater — is Rust `resolve_channel` in
 * `src-tauri/src/updater.rs` (fail-closed, compliance-critical, APP-6113 §5.4).
 * This copy exists ONLY for deployment-inventory telemetry + Flagsmith
 * targeting (APP-6116 §5.11); it MUST NOT be used as an update gate.
 *
 * SE-AZ1 is classified `'eu'` here AND in the Rust `resolve_channel` (the Rust
 * `Region` enum gained a `SeAz1` variant → `Channel::Named("eu")` in the same
 * PR, APP-6116 review), so the telemetry/targeting channel here and the
 * updater's actual channel agree — no divergence.
 *
 * Per design §5.3: any new EU/UK cell region must be classified **here and in
 * the Rust map in the same change**, or that cell's regulated users would be
 * silently tagged as ROW (and, in Rust, escape the freeze). The exhaustive
 * `switch` below turns a new `Region` value into a compile error here;
 * `channel.test.ts` asserts every controlled region resolves to a controlled
 * channel.
 */
export const CONTROLLED_REGIONS: readonly Region[] = [
  Region.UK,
  Region.EU,
  Region.SE_AZ1,
  Region.CH,
];

/**
 * Resolve the update channel for an authenticated region, mirroring Rust
 * `resolve_channel`. Returns `undefined` when the region is unresolved (web,
 * pre-login, or not yet fetched) so telemetry omits the dimension rather than
 * guessing — callers treat `undefined` as "unknown", never as ROW/`'stable'`.
 *
 * `region` MUST originate from `kindeProperties.region` (the authenticated
 * identity region), never `getRegion()` / `useRegion()` which fail open to AU
 * and honour `?region=` (C10) — for the inventory to be trustworthy the channel
 * must reflect the same region the Rust updater binds.
 */
export function resolveUpdateChannel(
  region: Region | undefined
): UpdateChannel | undefined {
  if (!region) return undefined;
  switch (region) {
    case Region.UK:
      return 'uk';
    // SE-AZ1 (Sweden, Enterprise) is an EU member state → EU-MDR controlled,
    // consistent with flags.ts / the europeanUnion coding config / useEvidenceAccess.
    case Region.EU:
    case Region.SE_AZ1:
    // CH (Switzerland) is MDR-aligned → EU-controlled, consistent with the
    // europeanUnion coding config. TODO: apps pod to confirm classification.
    case Region.CH:
      return 'eu';
    case Region.US:
    case Region.AU:
    case Region.CA:
    case Region.JP_AZ1:
    case Region.AU_AZ1:
    case Region.UAE_AZ1:
    case Region.US_AZ1:
    case Region.QAT_AZ1:
      return 'stable';
    default: {
      // Exhaustiveness guard: a new `Region` enum value is a compile error here,
      // forcing a conscious controlled-vs-ROW classification (design §5.3).
      const _exhaustive: never = region;
      return _exhaustive;
    }
  }
}
